import { roomCodeFromScannedText } from '@chipless/shared';
import { Alert, Button, Group, Modal, Stack, Text } from '@mantine/core';
import jsQR from 'jsqr';
import { useEffect, useRef, useState } from 'react';
import classes from './QrScanner.module.css';

/**
 * 中央ディスプレイの QR コードをカメラで読み取って参加する（要件1.11）。
 *
 * QR を OS のカメラアプリで開けばブラウザが起動して参加 URL へ飛べるが、卓に着いてから
 * アプリを開いている人はすでにブラウザにいる。そこからコードを目で読んで打ち込む手間を
 * 省くための経路。手入力は残す（カメラを許可したくない場合と、QR が見えない席のため）。
 *
 * デコードは純 JS の jsQR で行う。標準の `BarcodeDetector` は iOS 18 以降の WebKit で
 * 動作しないため使えない（WebKit バグ 281848）。wasm 版のデコーダは CDN からの取得が
 * 前提になり、卓上の不安定な回線で外部依存を増やす。読み取る対象は画面に大きく表示された
 * 高コントラストの QR なので、純 JS のデコード品質で足りる。
 */

/** 走査の間隔。毎フレーム解析するとバッテリーを食うだけで読み取りは速くならない。 */
const SCAN_INTERVAL_MS = 120;

/** 解析に使うフレームの最大辺。大きくしても読み取り率は上がらず、解析時間だけ伸びる。 */
const MAX_FRAME_EDGE = 640;

type Failure =
  /** ブラウザにカメラの API が無い。HTTP 経由での配信（安全なコンテキストでない）を含む。 */
  | 'unsupported'
  /** 利用者が許可しなかった。 */
  | 'denied'
  /** カメラが見つからない、または他のアプリが掴んでいる。 */
  | 'unavailable';

const FAILURE_MESSAGE: Record<Failure, string> = {
  unsupported:
    'この環境ではカメラを使えません。ルームコードの手入力で参加してください。（カメラは HTTPS でのみ使えます）',
  denied:
    'カメラの使用が許可されていません。ブラウザの設定で許可するか、ルームコードの手入力で参加してください。',
  unavailable:
    'カメラを開けませんでした。他のアプリが使用中でないか確認するか、ルームコードの手入力で参加してください。',
};

function classifyError(e: unknown): Failure {
  const name = e instanceof Error ? e.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  return 'unavailable';
}

export function QrScanner({
  opened,
  onClose,
  onDetect,
}: {
  opened: boolean;
  onClose: () => void;
  /** 読み取れたルームコード。正しい形式のものだけが渡る。 */
  onDetect: (roomCode: string) => void;
}): React.ReactElement {
  return (
    <Modal opened={opened} onClose={onClose} title="QR コードを読み取る" centered>
      <Stack>
        {/*
          開いている間だけ中身を持つ。カメラの起動と停止を「この要素の生存期間」に一致
          させることで、閉じ忘れによる撮影の継続が起きない。状態も開くたびに作り直される。
        */}
        {opened && <CameraView onDetect={onDetect} />}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            閉じる
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function CameraView({ onDetect }: { onDetect: (roomCode: string) => void }): React.ReactElement {
  const video = useRef<HTMLVideoElement | null>(null);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  /** 読み取れたが解釈できなかったことを伝える。無反応と区別が付かないと構え続けてしまう。 */
  const [ignored, setIgnored] = useState(false);

  // `onDetect` は呼び出し側で安定させる（useCallback）。毎レンダリングで別の関数が来ると
  // 走査が再起動し、カメラが開き直される。
  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    let stopped = false;

    const stop = (): void => {
      stopped = true;
      if (timer !== null) clearInterval(timer);
      timer = null;
      for (const track of stream?.getTracks() ?? []) track.stop();
      stream = null;
      if (video.current !== null) video.current.srcObject = null;
    };

    const scan = (): void => {
      const el = video.current;
      const board = canvas.current;
      if (el === null || board === null) return;
      if (el.readyState < el.HAVE_CURRENT_DATA) return;

      const scale = Math.min(1, MAX_FRAME_EDGE / Math.max(el.videoWidth, el.videoHeight));
      const width = Math.round(el.videoWidth * scale);
      const height = Math.round(el.videoHeight * scale);
      if (width === 0 || height === 0) return;

      board.width = width;
      board.height = height;
      const ctx = board.getContext('2d', { willReadFrequently: true });
      if (ctx === null) return;
      ctx.drawImage(el, 0, 0, width, height);

      const frame = ctx.getImageData(0, 0, width, height);
      // 白地に黒の QR しか出さないので反転は試さない。試すと解析が倍かかる。
      const found = jsQR(frame.data, width, height, { inversionAttempts: 'dontInvert' });
      if (found === null) return;

      const roomCode = roomCodeFromScannedText(found.data);
      if (roomCode === null) {
        // Chipless の QR ではない。走査は続ける。
        setIgnored(true);
        return;
      }
      stop();
      onDetect(roomCode);
    };

    const start = async (): Promise<void> => {
      if (globalThis.navigator?.mediaDevices?.getUserMedia === undefined) {
        setFailure('unsupported');
        return;
      }
      try {
        const media = await globalThis.navigator.mediaDevices.getUserMedia({
          // スマホでは背面カメラを使う。卓の中央にある画面へ向ける動作になる。
          video: { facingMode: { ideal: 'environment' } },
        });
        if (stopped) {
          for (const track of media.getTracks()) track.stop();
          return;
        }
        stream = media;
        if (video.current !== null) {
          video.current.srcObject = media;
          await video.current.play().catch(() => undefined);
        }
        timer = setInterval(scan, SCAN_INTERVAL_MS);
      } catch (e) {
        if (!stopped) setFailure(classifyError(e));
      }
    };

    void start();
    return stop;
  }, [onDetect]);

  if (failure !== null) {
    return (
      <Alert color="orange" data-testid="qr-failure" data-failure={failure}>
        {FAILURE_MESSAGE[failure]}
      </Alert>
    );
  }

  return (
    <>
      <Text size="sm" c="dimmed">
        中央ディスプレイの QR コードにカメラを向けてください。
      </Text>
      <div className={classes.frame}>
        {/*
          iOS では playsInline が無いと全画面再生に切り替わり、走査が続かない。
          muted はユーザー操作なしの自動再生を通すために必要。
        */}
        <video
          ref={video}
          className={classes.video}
          autoPlay
          playsInline
          muted
          data-testid="qr-video"
        />
      </div>
      {ignored && (
        <Text size="sm" c="yellow.4" data-testid="qr-ignored">
          Chipless の QR コードではありません。中央ディスプレイの QR を写してください。
        </Text>
      )}
      {/* 解析用のフレームを描く先。表示はしない。 */}
      <canvas ref={canvas} className={classes.hidden} />
    </>
  );
}
